import { inject, Injectable } from '@angular/core'
import { TranslocoService } from '@jsverse/transloco'
import { MessageService, ToastMessageOptions } from 'primeng/api'
import { ResultCRUD, ToastDetailOptions, ToastOptions, ToastSeverity } from '../../models/functional/toastState'

@Injectable({
  providedIn: 'root',
})
export class PrimeNgToastService {
  private readonly translocoService = inject(TranslocoService)
  private readonly messageService = inject(MessageService)

  /**
   * Display a CRUD toast message with specified result, prefix, and toast options.
   * @param result - The CRUD result object containing status, action, and other details.
   * @param prefix - The translation prefix for the toast message. Every translation key should be in format "prefix.CRUD.ACTION.STATUS", e.g., "TOAST.CRUD.CREATE.SUCCESS" or "TOAST.CRUD.UPDATE.ERROR"
   * @param toastOptions - The options for the toast message, including duration and icon.
   */
  displayCrudToast(result: ResultCRUD, prefix: string, toastOptions?: ToastOptions): void {
    this.messageService.add({
      severity: result.status,
      summary: this.translocoService.translate(this.getSummaryKey(result.status)),
      detail: this.translocoService.translate(
        `${prefix}.CRUD.${result.action.toUpperCase()}.${result.status.toUpperCase()}`,
      ),
      ...this.constructToastOptions(toastOptions),
    })
  }

  /**
   * Display a custom toast message with specified severity, detail options, and toast options.
   * @param severity - The severity level of the toast message.
   * @param detailOptions - The options for the toast message detail or translation key itself.
   * @param toastOptions - The options for the toast message, including durationMs, icon, and styleClass.
   */
  displayCustomToast(
    severity: ToastSeverity,
    detailOptions: ToastDetailOptions | string,
    toastOptions?: ToastOptions,
  ): void {
    this.messageService.add({
      severity,
      summary: this.translocoService.translate(this.getSummaryKey(severity)),
      detail: this.constructDetailTranslation(detailOptions),
      ...this.constructToastOptions(toastOptions),
    })
  }

  private getSummaryKey(severity: ToastSeverity): string {
    return `TOAST.${severity.toUpperCase()}`
  }

  private constructDetailTranslation(detailOptions: string | ToastDetailOptions): string {
    if (typeof detailOptions === 'string') {
      return this.translocoService.translate(detailOptions)
    }
    return this.translocoService.translate(detailOptions.key, detailOptions.params ?? {})
  }

  private constructToastOptions(toastOptions: ToastOptions): Partial<ToastMessageOptions> {
    return {
      life: toastOptions?.durationMs ?? 3000,
      ...(toastOptions?.icon && { icon: toastOptions.icon }),
      ...(toastOptions?.styleClass && { styleClass: toastOptions.styleClass }),
    }
  }
}
